import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { ErrorBanner } from './auth-form-helpers';
import { passkeysSupported } from '../api/passkeys';
import { useSocialProviders } from '../api/social-auth';
import { SocialAuthButtons } from './SocialAuthButtons';
import { PasskeySignInButton } from './PasskeySignInButton';

interface SignInFormProps {
  onCreateAccount: () => void;
  onSuccess: () => void;
}

export function SignInForm({ onSuccess, onCreateAccount }: SignInFormProps) {
  const { login } = useAuth();
  const { data: socialProviders } = useSocialProviders();
  const hasAlternatives = Boolean(socialProviders?.length) || passkeysSupported();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const inFlight = useRef(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      onSuccess();
    } catch {
      setError('Invalid email or password');
    } finally {
      inFlight.current = false;
      setIsLoading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Above the form, and it renders nothing when no provider is configured
          — so an install without Google or Apple set up looks exactly as it did
          before, with no empty row or stray divider. */}
      <SocialAuthButtons onError={setError} onSuccess={onSuccess} onNeedsOrganization={onCreateAccount} />
      <PasskeySignInButton onError={setError} onSuccess={onSuccess} />

      {/* After every alternative, not in the middle of them. The passkey button
          previously rendered below this, which read as part of the email
          section rather than as a third way in. Hidden entirely when there is
          nothing above it to divide from. */}
      {hasAlternatives && (
        <div className="mb-2 flex items-center gap-3">
          <span className="h-px flex-1 bg-border" />
          <span className="font-mono text-[11px] tracking-[0.08em] text-muted-foreground uppercase">
            Or continue with email
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>
      )}

      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {error && <ErrorBanner message={error} />}
        <div className="flex flex-col gap-2">
          <Label htmlFor="signin-email">Email</Label>
          <Input
            id="signin-email"
            ref={emailInputRef}
            required
            type="email"
            value={email}
            autoComplete="email"
            placeholder="you@example.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="signin-password">Password</Label>
          {/* Matching ChangePasswordDialog's PasswordField rather than adding a
              second reveal style. `tabIndex={-1}` keeps the toggle out of the
              path from password to Sign in. */}
          <div className="relative">
            <Input
              id="signin-password"
              required
              value={password}
              className="pr-10"
              autoComplete="current-password"
              type={passwordVisible ? 'text' : 'password'}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              tabIndex={-1}
              type="button"
              aria-label={passwordVisible ? 'Hide password' : 'Show password'}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
              onClick={() => setPasswordVisible((v) => !v)}>
              {passwordVisible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sign in
        </Button>
      </form>
      <div className="border-t pt-4 text-center text-sm text-muted-foreground">
        New here?{' '}
        <button
          type="button"
          className="font-medium text-primary underline-offset-4 hover:underline"
          onClick={onCreateAccount}>
          Create an account
        </button>
      </div>
    </div>
  );
}
